<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CompanyBankAccount',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    parent: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const resource = ref(null);
  const editor = ref(null);
  const createTitle = ref(
    $fn.value.pgettext('CompanyBankAccounts', 'Add bank account')
  );
  const editTitle = ref(
    $fn.value.pgettext('CompanyBankAccounts', 'Edit bank account')
  );
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    resource: resource.value,
    editor: editor.value,
    createTitle: createTitle.value,
    editTitle: editTitle.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_resource = async () => {
    return props.item || $fn.value.schemaDefaults('CreateBankAccount');
  };
  const $$load_editor = async () => {
    return resource.value.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'createTitle' },
      { name: 'editTitle' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { resource: $$load_resource, editor: $$load_editor },
    ux: {},
    dataRefs: {
      resource,
      editor,
      createTitle,
      editTitle,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: props.item == null ? createTitle.value : editTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );
  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_zjz7pgmfn_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_izs9s5lrr_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_closeComponent_1di49c38s_getActionArgs = async (
    $event
  ) => {
    return {
      result: {},
    };
  };
  const act_1040167445267876_rest_lwq9rw50u_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: props.parent.bank_accounts,
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_zjz7pgmfn =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_zjz7pgmfn_getActionArgs,
    });
  const inst_evh_7315092382398562_close_qczrvx4sg =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_zjz7pgmfn,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_qczrvx4sg],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_c08l4eqih =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_4l5v3qplx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_c08l4eqih,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_4l5v3qplx],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_2r4112a2k =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_i3y6rjbyo =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_2r4112a2k,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_i3y6rjbyo],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_izs9s5lrr =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_izs9s5lrr_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_9d1iguwj4 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_izs9s5lrr,
      event: 'saved',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_closeComponent_1di49c38s =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_1di49c38s_getActionArgs,
    });
  const inst_evh_1040167445267876_deleted_xte0xnxjk =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_1di49c38s,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_rest_lwq9rw50u = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_lwq9rw50u_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_saved_9d1iguwj4,
      inst_evh_1040167445267876_deleted_xte0xnxjk,
    ],
  });
  const inst_evh_1040167445267876_submit_v1ldhayc1 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_lwq9rw50u,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_v1ldhayc1],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_q34wp0a6u =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_3jp1fjdpg =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_q34wp0a6u,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_3jp1fjdpg],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_qczrvx4sg,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_4l5v3qplx,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_i3y6rjbyo,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_saved_9d1iguwj4,
    inst_evh_1040167445267876_deleted_xte0xnxjk,
    inst_evh_1040167445267876_submit_v1ldhayc1,
    submit: inst_evh_1040167445267876_submit_v1ldhayc1,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_3jp1fjdpg,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_qczrvx4sg = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_qczrvx4sg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_4l5v3qplx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_4l5v3qplx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_i3y6rjbyo = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_i3y6rjbyo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_9d1iguwj4 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_9d1iguwj4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_deleted_xte0xnxjk = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_deleted_xte0xnxjk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_v1ldhayc1 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_v1ldhayc1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_3jp1fjdpg = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_3jp1fjdpg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CompanyBankAccount"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="bankAccount"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <UcCompanyBankAccountFieldset :bankAccount="editor">
        </UcCompanyBankAccountFieldset>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.bankAccount"
          :text="gettext('Save')"
          type="submit"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
